import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const MiniTable = ({ head, rows }) => (
  <div className="mt-3 rounded-lg border border-white/10 overflow-hidden">
    <div className="grid bg-[#FFD700]/[0.07]" style={{ gridTemplateColumns: `repeat(${head.length}, 1fr)` }}>
      {head.map((h) => (
        <div key={h} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#FFD700]">{h}</div>
      ))}
    </div>
    {rows.map((r, i) => (
      <div key={i} className="grid border-t border-white/5" style={{ gridTemplateColumns: `repeat(${head.length}, 1fr)` }}>
        {r.map((c, j) => (
          <div key={j} className="px-3 py-2 text-xs text-[#DBDFE8]">{c}</div>
        ))}
      </div>
    ))}
  </div>
);

const SIZES = ["$5,000", "$10,000", "$25,000", "$50,000", "$100,000"];

const GROUPS = [
  {
    heading: "About the Swing 2-Step Challenge",
    items: [
      ["What is the Swing 2-Step Challenge?",
       "It is a two-phase evaluation. Pass Phase 1 and Phase 2, and you move to a Funded account with an 80% profit split."],
      ["Which account sizes can I choose?",
       "Five sizes: $5,000, $10,000, $25,000, $50,000 and $100,000. All rules are the same percentage for every size; only the dollar amounts change."],
      ["Why is it called “Swing”?",
       "The plan is built for traders who hold positions longer. Overnight and weekend holding are allowed, and so is trading around major news."],
    ],
  },
  {
    heading: "Profit Targets",
    items: [
      ["What profit do I need to pass each phase?",
       "Phase 1 requires 10% profit. Phase 2 requires 5% profit.",
       { head: ["Account size", "Phase 1 target (10%)", "Phase 2 target (5%)"],
         rows: SIZES.map((s, i) => [s, ["$500", "$1,000", "$2,500", "$5,000", "$10,000"][i], ["$250", "$500", "$1,250", "$2,500", "$5,000"][i]]) }],
    ],
  },
  {
    heading: "Loss Limits",
    items: [
      ["What are the loss limits?",
       "There are two: a Maximum Daily Loss of 5% of your balance at the start of each day, and a Maximum Loss of 10% of the initial account size. Breaching either one ends the account. The daily loss amounts below apply when your balance equals the account size — the actual daily limit is recalculated every day as 5% of your balance at 00:00 UTC, so it grows with your profits and shrinks after losses.",
       { head: ["Account size", "Max Daily Loss (5%)", "Max Loss (10%)"],
         rows: SIZES.map((s, i) => [s, ["$250", "$500", "$1,250", "$2,500", "$5,000"][i], ["$500", "$1,000", "$2,500", "$5,000", "$10,000"][i]]) }],
      ["Do the loss limits apply in every phase?",
       "Yes. The same limits apply in Phase 1, Phase 2 and on the Funded account."],
    ],
  },
  {
    heading: "How Drawdown Is Calculated",
    items: [
      ["Is drawdown measured on balance or on equity?",
       "On equity. Equity is your balance plus the floating profit or loss of your open positions, so open trades count towards both loss limits at every moment, not only when you close them."],
      ["How does the Maximum Daily Loss work?",
       "The trading day starts at 00:00 UTC. Your balance at that moment, from closed trades only, is the starting point for the day. Your daily limit is 5% of that starting balance. During the day your equity, including open positions, must not drop by more than this amount below the starting point. If it does, the account is failed. Example on a $10,000 account: at 00:00 UTC your balance is $10,200, so your daily limit is $510 (5% × $10,200). During that day your equity must stay above $9,690 ($10,200 − $510), even if a trade is still open. At the next 00:00 UTC the starting point and the limit are set again from your balance at that moment."],
      ["How does the Maximum Loss work?",
       "The Maximum Loss level is fixed. It is calculated once from your initial balance and never changes, even when you are in profit. If your equity drops to or below this level at any time, the account is failed. Example: on a $10,000 account you grow equity to $10,800 — the Maximum Loss level stays at $9,000; it does not move up with your profit.",
       { head: ["Account size", "Max Loss (10%)", "Equity must stay above"],
         rows: SIZES.map((s, i) => [s, ["$500", "$1,000", "$2,500", "$5,000", "$10,000"][i], ["$4,500", "$9,000", "$22,500", "$45,000", "$90,000"][i]]) }],
      ["How do the two limits work together?",
       "They are separate limits checked at the same time. The account is failed when either one is reached first. A day with a large loss still counts towards the Maximum Loss, so you can reach the Maximum Loss without ever breaking the daily limit."],
      ["Where can I see my current limits?",
       "Your dashboard shows the live levels of both limits for your account."],
    ],
  },
  {
    heading: "Trading Days and Time Limits",
    items: [
      ["How many trading days do I need?",
       "At least 5 trading days in Phase 1 and at least 5 in Phase 2. The Funded account has no minimum trading days."],
      ["Is there a time limit to complete the challenge?",
       "No. The trading period is unlimited, so you can take as long as you need to reach the profit target."],
    ],
  },
  {
    heading: "Funded Account",
    items: [
      ["What happens after I pass both phases?",
       "You receive a Funded account of the same size. The same loss limits stay in place, there is no minimum number of trading days, and leverage is lower than in the challenge phases (see Trading Conditions)."],
      ["What is the profit split?",
       "80% / 20%: you keep 80% of the profit you make on the Funded account."],
      ["How often can I request a payout?",
       "On the Funded account you can request a payout every 5 days."],
    ],
  },
  {
    heading: "Trading Conditions",
    items: [
      ["What leverage do I get?",
       "Leverage is set per instrument and is lower than on standard 2-Step accounts, which reduces the risk of holding positions for longer. The Funded account has lower leverage than Phase 1 and Phase 2. The leverage for every instrument is shown in the symbol details on the platform.",
       { head: ["Instruments", "Phase 1 & 2", "Funded"],
         rows: [
           ["FX pairs", "1:40", "1:30"],
           ["Indices and gold (XAUUSD)", "1:20", "1:15"],
           ["Other metals (silver, platinum, palladium, copper, aluminium)", "1:15", "1:10"],
           ["Energies and agricultural commodities", "1:10", "1:5"],
           ["Crypto and stocks", "1:5", "1:3"],
         ] }],
      ["Can I hold trades overnight and over the weekend?",
       "Yes. Overnight and weekend holding are allowed on all Swing accounts."],
      ["Can I trade during major news releases?",
       "Yes. Trading around major news is allowed."],
      ["How is the weekend swap charged?",
       "Swap is charged once for each night a position stays open from Monday to Thursday. On Friday night a triple swap is charged to cover the weekend, and no swap is charged on Saturday or Sunday. This applies in Phase 1, Phase 2 and on the Funded account."],
    ],
  },
  {
    heading: "Pricing",
    items: [
      ["How much does the challenge cost?",
       "One-time fee per account size:",
       { head: ["Account size", "Challenge price"],
         rows: SIZES.map((s, i) => [s, ["$65", "$110", "$240", "$375", "$750"][i]]) }],
    ],
  },
];

export default function SwingFAQ() {
  return (
    <main className="pt-32 pb-24 lg:pb-32" data-testid="swing-faq-page">
      <Seo
        title="Swing 2-Step Challenge FAQ — MyFXPocket"
        description="Swing challenge conditions: overnight and weekend holding, news trading allowed, unlimited time, per-instrument leverage and pricing from $65."
        path="/swing-faq"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">Swing 2-Step</span>
            <span className="h-px w-10 bg-gradient-to-r from-[#FFD700] to-transparent" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#999999]">Challenge & Funded Account Conditions</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-[-0.05em] text-white leading-[1.1]">
            Swing 2-Step <span className="gold-text">FAQ.</span>
          </h1>
          <p className="mt-5 text-base text-[#999999] leading-relaxed max-w-2xl">
            Built for traders who hold positions longer — overnight and weekend holding allowed,
            news trading allowed, unlimited time to pass.
          </p>
          <div className="mt-6 space-y-4 text-sm sm:text-base text-[#999999] leading-relaxed max-w-3xl">
            <p>
              The Swing 2-Step Challenge is a two-phase forex evaluation designed for swing traders.
              Phase 1 requires a 10% profit target and Phase 2 requires 5%, with a 5% maximum daily
              loss measured on equity from your 00:00 UTC balance and a fixed 10% maximum loss from
              the initial account size. Accounts are available in five sizes — $5,000, $10,000,
              $25,000, $50,000 and $100,000 — priced from $65 to $750 as a one-time fee, with at
              least five trading days per phase and no time limit to complete the challenge.
            </p>
            <p>
              Unlike standard challenges, Swing accounts permit overnight holding, weekend holding
              (with a triple Friday swap) and trading around major news releases, with per-instrument
              leverage calibrated for longer holding periods. Pass both phases and you receive a
              funded account of the same size with an 80/20 profit split, the same loss limits, no
              minimum trading days and payouts available every 5 days.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 space-y-12">
          {GROUPS.map((g, gi) => (
            <Reveal key={g.heading} delay={0.05}>
              <section data-testid={`swing-faq-group-${gi}`}>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-[-0.03em] text-white mb-5 flex items-baseline gap-3">
                  <span className="text-xs font-bold text-[#FFD700]">{String(gi + 1).padStart(2, "0")}</span>
                  {g.heading}
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {g.items.map(([q, a, table], i) => (
                    <AccordionItem
                      key={i}
                      value={`sw-${gi}-${i}`}
                      data-testid={`swing-faq-item-${gi}-${i}`}
                      className="glass-card rounded-xl border-white/5 px-5 sm:px-6 overflow-hidden"
                    >
                      <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-white hover:text-[#FFD700] hover:no-underline py-5 [&[data-state=open]]:text-[#FFD700]">
                        {q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-[#999999] leading-relaxed pb-5">
                        {a}
                        {table && <MiniTable head={table.head} rows={table.rows} />}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
